import React, { useEffect, useState } from "react";
import ChallengeDetailModal from "./ChallengeDetailModal";

const API_BASE = process.env.REACT_APP_HOST || "";

function Innovation() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [updateStatus, setUpdateStatus] = useState({
    id: null, 
    loading: false, 
    message: "",
    type: "" 
  });
  const [filter, setFilter] = useState({
    status: "all",
    public: "all"
  });
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  useEffect(() => {
    fetchGoogleSheetData();
  }, []);

  const fetchGoogleSheetData = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        sheetNo: "Challenge Post",
      });

      const response = await fetch(
        `${API_BASE}/medtech-challenges?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.status === "success" && Array.isArray(result.data)) {
        // Show newest received rows first based on timestamp/date
        const sorted = [...result.data].sort((a, b) => {
          const dateA = new Date(a.Date || a.Timestamp || 0);
          const dateB = new Date(b.Date || b.Timestamp || 0);
          return dateB - dateA;
        });

        setData(sorted);
      } else {
        setError(result.message || "Failed to fetch data");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Server error while fetching data");
    } finally {
      setLoading(false);
    }
  };

  const updateRow = async (id, updatedFields) => {
    setActionError("");
    if (!id) {
      console.error("Missing ID for row update", updatedFields);
      setActionError("Cannot update this entry because it has no ID.");
      return;
    }
    // A challenge can only be made public once it has been approved.
    const targetRow = data.find((row) => row.ID === id);
    if (
      updatedFields.Visibility === "TRUE" &&
      (targetRow?.status || "").toLowerCase() !== "approved"
    ) {
      setActionError("This challenge must be approved before it can be made public.");
      return;
    }

    // Set loading state for this row
    setUpdateStatus({
      id,
      loading: true,
      message: "Updating...",
      type: "info"
    });

    // Optimistic UI update: sending a challenge back to pending also unpublishes it.
    setData((prevData) =>
      prevData.map((row) =>
        row.ID === id
          ? {
              ...row,
              ...updatedFields,
              ...(updatedFields.status === "pending" && { Visibility: "FALSE" }),
            }
          : row
      )
    );

    try {
      // Ensure phone number is treated as plain text in Google Sheets
      if (updatedFields.PhoneNumber) {
        const rawPhone = updatedFields.PhoneNumber.toString().replace(/^'/, "");
        updatedFields.PhoneNumber = `'${rawPhone}`;
      }

      const response = await fetch(`${API_BASE}/medtech-challenges`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sheetNo: "Challenge Post",
          data: {
            ID: id,
            ...updatedFields,
          },
        }),
      });

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        console.error("Failed to parse medtech-challenges response", parseError);
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      if (!response.ok) {
        console.error("medtech-challenges returned error", result);
        throw new Error(result.message || `HTTP ${response.status} ${response.statusText}`);
      }
      
      if (result.status === "success") {
        setUpdateStatus({ 
          id, 
          loading: false, 
          message: "✓ Updated successfully", 
          type: "success" 
        });
        
        // Clear success message after 2 seconds
        setTimeout(() => {
          setUpdateStatus({ id: null, loading: false, message: "", type: "" });
        }, 2000);
      } else {
        throw new Error(result.message || "Update failed");
      }

      // Refresh data to ensure consistency
      fetchGoogleSheetData();
    } catch (error) {
      console.error("Update failed:", error);
      setUpdateStatus({
        id,
        loading: false,
        message: "✗ Update failed",
        type: "error"
      });
      setActionError(error.message || "Server error while updating data");

      // Reload data from server to fix optimistic state
      fetchGoogleSheetData();

      // Clear error status after 3 seconds
      setTimeout(() => {
        setUpdateStatus({ id: null, loading: false, message: "", type: "" });
        setActionError("");
      }, 3000);
    }
  };

  const getFilteredData = () => {
    return data.filter(row => {
      // Filter by status
      if (filter.status !== "all") {
        if (filter.status === "pending" && (row.status || "").toLowerCase() !== "pending") {
          return false;
        }
        if (filter.status === "approved" && (row.status || "").toLowerCase() !== "approved") {
          return false;
        }
      }
      
      // Filter by public status (Visibility column in sheet)
      if (filter.public !== "all") {
        const isPublic = (row.Visibility || "").toString().toLowerCase() === "true";
        if (filter.public === "public" && !isPublic) {
          return false;
        }
        if (filter.public === "private" && isPublic) {
          return false;
        }
      }
      
      return true;
    });
  };

  const clearFilters = () => {
    setFilter({ status: "all", public: "all" });
  };

  const filteredData = getFilteredData();

  return (
    <div className="innovation-page">
      <div className="innovation-header">
        <h2 className="innovation-title">Healthcare Challenges</h2>
        <button 
          className="innovation-refresh-btn"
          onClick={fetchGoogleSheetData}
          disabled={loading}
        >
          <span className="refresh-icon">↻</span>
          Refresh Data
        </button>
      </div>

      {/* Filter Bar */}
      <div className="innovation-filter-bar">
        <div className="filter-group">
          <label>Status:</label>
          <select 
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">🟡 Pending</option>
            <option value="approved">🟢 Approved</option>
          </select>
          
        </div>

        <div className="filter-group">
          <label>Visibility:</label>
          <select 
            value={filter.public}
            onChange={(e) => setFilter({ ...filter, public: e.target.value })}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="public">🌍 Public</option>
            <option value="private">🔒 Private</option>
          </select>
        </div>

        {(filter.status !== "all" || filter.public !== "all") && (
          <button className="filter-clear-btn" onClick={clearFilters}>
            Clear Filters
          </button>
        )}

        <div className="filter-stats">
          Showing {filteredData.length} of {data.length} entries
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="innovation-loading">
          <div className="loading-spinner"></div>
          <p>Loading challenge data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="innovation-error">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchGoogleSheetData} className="error-retry-btn">
            Retry
          </button>
        </div>
      )}

      {/* Row action error */}
      {actionError && !error && (
        <div className="innovation-error">
          <span className="error-icon">⚠️</span>
          <p>{actionError}</p>
        </div>
      )}

      {/* Data Table */}
      {!loading && !error && (
        <div className="innovation-table-wrapper">
          <table className="innovation-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Organization</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Title</th>
                <th>Job Title</th>
                <th>Description</th>
                <th>Status</th>
                <th>Public</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="11" className="no-data">
                    <div className="no-data-message">
                      📋 No challenges found matching the current filters
                    </div>
                  </td>
                </tr>
              ) : (
                filteredData.map((row, index) => {
                  const isUpdating = updateStatus.id === row.ID && updateStatus.loading;
                  const statusLower = (row.status || "").toString().toLowerCase();
                  const isPublicLower = (row.Visibility || "").toString().toLowerCase();
                  const isApproved = statusLower === "approved";
                  
                  return (
                    <tr key={row.ID || index} className={isUpdating ? "updating-row" : ""}>
                      <td>
                        <span className="date-cell">
                          {row.Date ? (
                            <>
                              {new Date(row.Date).toLocaleDateString()}
                            </>
                          ) : (
                            "N/A"
                          )}
                        </span>
                      </td>
                      <td>
                        <span className="type-badge">
                          {row.type || "CHALLENGE"}
                        </span>
                      </td>
                      <td className="organization-cell">{row.OrganizationName || "—"}</td>
                      <td>{row.ContactName || "—"}</td>
                      <td className="email-cell">
                        {row.Email ? (
                          <a href={`mailto:${row.Email}`} className="email-link">
                            {row.Email}
                          </a>
                        ) : "—"}
                      </td>
                      <td className="phone-cell">
                        {row.PhoneNumber ? (
                          <span className="phone-number">
                            {row.PhoneNumber.startsWith("'") 
                              ? row.PhoneNumber.substring(1) 
                              : row.PhoneNumber}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="title-cell">{row.Title || "—"}</td>
                      <td>{row.JobTitle || "—"}</td>
                      <td className="description-cell">
                        {row.ChallengeDescription && (
                          <button
                            type="button"
                            className="description-view-btn"
                            onClick={() => setSelectedChallenge(row)}
                          >
                            View
                          </button>
                        )}
                      </td>

                      {/* STATUS DROPDOWN */}
                      <td>
                        <div className="status-cell">
                          <select
                            value={statusLower}
                            onChange={(e) => {
                              const newStatus = e.target.value;
                              const updatedFields = { 
                                status: newStatus,
                                // Auto-set Visibility to FALSE when pending
                                ...(newStatus === "pending" && { Visibility: "FALSE" }),
                                // Re-send phone number so it can be normalized as text
                                ...(row.PhoneNumber && { PhoneNumber: row.PhoneNumber })
                              };
                              updateRow(row.ID, updatedFields);
                            }}
                            className={`status-select ${statusLower}`}
                          >
                            <option value="pending" className="pending-option">
                              🟡 Pending
                            </option>
                            <option value="approved" className="approved-option">
                              🟢 Approved
                            </option>
                          </select>
                        </div>
                      </td>

                      {/* PUBLIC TOGGLE */}
                      <td>
                        <div className="public-cell">
                          <button
                            type="button"
                            className={`toggle-btn ${!(isPublicLower === "true") ? "" : "active"}`}
                            disabled={!isApproved || isUpdating}
                            onClick={() => {
                              updateRow(row.ID, {
                                Visibility: isPublicLower === "true" ? "FALSE" : "TRUE",
                                // Re-send phone number so it can be normalized as text
                                ...(row.PhoneNumber && { PhoneNumber: row.PhoneNumber })
                              });
                            }}
                          >
                            <span className="toggle-track">
                              <span className={`toggle-thumb ${isPublicLower === "true" ? "right" : "left"}`} />
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
      <ChallengeDetailModal
        challenge={selectedChallenge}
        onClose={() => setSelectedChallenge(null)}
      />
    </div>
  );
}

export default Innovation;
